import React, { memo, useReducer, useEffect } from 'react';
import { Form } from '@redlink/components/organisms';
import { Space, withPopUp } from '@redlink/components/atoms';
import {
  FormSection,
  TextInput,
  Checkbox,
  TagInput,
} from '@redlink/components/molecules';
import { DataEntryModalTemplate } from '@redlink/components/templates';
import { useLocale } from '@redlink/assets/locale';
import {
  useMutation,
  newUserQuery,
  editUserQuery,
  generateNewShortLinkQuery,
} from '@redlink/services';
import { useValues } from '@redlink/assets/values';
import { initialState, reducer, types } from './store';

export const AdminNewUserPage = withPopUp(
  memo(({ onDismiss, data, onSubmit }) => {
    const { dictionary } = useLocale();
    const { constants } = useValues();

    const [
      {
        id,
        isEditMode,
        fullname,
        username,
        password,
        pageName,
        tags,
        isPro,
        bio,
      },
      dispatch,
    ] = useReducer(reducer, initialState(data));

    const [newShortLink, { loading: newShortLinkLoading }] = useMutation(
      generateNewShortLinkQuery,
      {
        done: ({ GenerateNewShortLink: { token } }) =>
          dispatch({ type: types.SET_PAGE_NAME, payload: token }),
      }
    );

    const [submitUser, { loading: submitUserIsLoading }] = useMutation(
      !isEditMode ? newUserQuery : editUserQuery,
      {
        done: () => onSubmit(),
      }
    );

    useEffect(() => {
      try {
        !isEditMode && newShortLink();
      } catch {}
    }, []);

    return (
      <DataEntryModalTemplate>
        <Form
          isBusy={newShortLinkLoading || submitUserIsLoading}
          actions={[
            {
              title: dictionary.cancel,
              variant: 'secondary',
              onPress: onDismiss,
            },
            {
              title: dictionary.add,
              variant: 'primary',
              onPress: () =>
                submitUser({
                  id,
                  shortLink: pageName,
                  username,
                  password,
                  fullname,
                  tags,
                  bio,
                  isPro,
                }),
            },
          ]}
        >
          <FormSection title={dictionary.newUser}>
            <TextInput
              disabled={isEditMode}
              en
              require
              value={username}
              title={dictionary.email}
              onChangeText={(value) =>
                dispatch({ type: types.SET_USERNAME, payload: value })
              }
            />
            <Space size={16} />
            <TextInput
              value={fullname}
              title={dictionary.name}
              onChangeText={(value) =>
                dispatch({ type: types.SET_FULLNAME, payload: value })
              }
            />
            <Space size={16} />
            <TextInput
              require
              en
              title={dictionary.pageName}
              prefix={constants.pageLinkPrefix}
              value={pageName}
              onChangeText={(value) =>
                dispatch({ type: types.SET_PAGE_NAME, payload: value })
              }
            />
            <Space size={16} />
            {!isEditMode && (
              <>
                <TextInput
                  require
                  value={password}
                  onChangeText={(value) =>
                    dispatch({ type: types.SET_PASSWORD, payload: value })
                  }
                  title={dictionary.password}
                  secureTextEntry
                />
                <Space size={16} />
              </>
            )}
            <TagInput
              title={dictionary.tags}
              data={tags}
              onAdd={(item) =>
                dispatch({ type: types.SET_TAGS, payload: [...tags, item] })
              }
              onRemove={(item) =>
                dispatch({
                  type: types.SET_TAGS,
                  payload: [...tags.filter((p) => p !== item)],
                })
              }
            />
            <Space size={16} />
            <TextInput
              multiline
              value={bio}
              title={dictionary.bio}
              onChangeText={(value) =>
                dispatch({ type: types.SET_BIO, payload: value })
              }
            />
            <Space size={16} />

            <Checkbox
              disabled={isEditMode}
              title={dictionary.isPro}
              value={isPro}
              onChange={(value) =>
                dispatch({ type: types.SET_IS_PRO, payload: value })
              }
            />
          </FormSection>
        </Form>
      </DataEntryModalTemplate>
    );
  })
);
